// app/components/Plugins/Automations/core/FlowEngine.ts

import { AutomationEdge } from '../types';
import { FlowProtocol, NodeProtocol, SerializedFlow, ValidationResult } from './protocols';

/**
 * Implementation of the FlowProtocol
 * Manages flow definition, validation, and traversal
 */
export class FlowEngine implements FlowProtocol {
  nodes: Map<string, NodeProtocol>;
  edges: AutomationEdge[];

  constructor() {
    this.nodes = new Map<string, NodeProtocol>();
    this.edges = [];
  }

  /**
   * Validate the flow configuration
   * Checks for valid nodes and edges
   */
  validate(): ValidationResult {
    const errors: string[] = [];
    
    // Validate each node
    this.nodes.forEach(node => {
      const nodeValidation = node.validate();
      if (!nodeValidation.valid) {
        errors.push(`Node ${node.id} (${node.type}) validation failed: ${nodeValidation.errors.join(', ')}`);
      }
    });
    
    // Validate edges
    this.edges.forEach(edge => {
      if (!this.nodes.has(edge.source)) {
        errors.push(`Edge ${edge.id} references non-existent source node ${edge.source}`);
      }
      if (!this.nodes.has(edge.target)) {
        errors.push(`Edge ${edge.id} references non-existent target node ${edge.target}`);
      }
    });
    
    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Serialize the flow for storage
   */
  serialize(): SerializedFlow {
    const serializedNodes = Array.from(this.nodes.values()).map(node => node.serialize());
    
    return {
      nodes: serializedNodes,
      edges: this.edges
    };
  }

  /**
   * Deserialize a flow from storage
   * This is a placeholder implementation that would need to be completed
   * with node factory logic to instantiate the correct node types
   */
  deserialize(data: SerializedFlow): void {
    // Clear existing data
    this.nodes.clear();
    this.edges = [];
    
    // This would need a node factory to create the appropriate node instances
    // For now, this is a placeholder
    
    // Add edges
    this.edges = [...data.edges];
  }
}